import React, { useState, useEffect } from "react";
import {
  Modal,
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Switch,
  Button,
  Table,
  Statistic,
  message
} from "antd";
import { FileTextOutlined, DollarOutlined } from "@ant-design/icons";
import moment from "moment";
import { ConfigProvider } from "antd";
import enUS from "antd/es/locale/en_US";
import billingApi from "../../billing/store/api";

const { RangePicker } = DatePicker;

// Create custom locale with Monday as first day of week
const mondayFirstLocale = {
  ...enUS,
  lang: {
    ...enUS.lang,
    weekStart: 1
  },
  DatePicker: {
    ...enUS.DatePicker,
    lang: {
      ...enUS.DatePicker.lang,
      weekStart: 1
    }
  }
};

// Custom RangePicker component with Monday-first week
const MondayFirstRangePicker = (props) => {
  const tempLocale = moment.localeData()._config;

  React.useEffect(() => {
    moment.updateLocale("en", {
      week: {
        dow: 1,
        doy: 4
      }
    });

    return () => {
      moment.updateLocale("en", tempLocale);
    };
  }, []);

  return (
    <ConfigProvider locale={mondayFirstLocale}>
      <RangePicker {...props} />
    </ConfigProvider>
  );
};

// Package status options - matching PACKAGE_STATUS enum from backend
const PACKAGE_STATUS_OPTIONS = [
  { label: "Submitted", value: "submitted" },
  { label: "Pending", value: "pending" },
  { label: "At Warehouse", value: "at_warehouse" },
  { label: "Pickup Scheduled", value: "pickup_scheduled" },
  { label: "Pickup In Progress", value: "pickup_in_progress" },
  { label: "Pickup Completed", value: "pickup_completed" },
  { label: "Pickup Failed", value: "pickup_failed" },
  { label: "Return Scheduled", value: "return_scheduled" },
  { label: "Return In Progress", value: "return_in_progress" },
  { label: "Return Completed", value: "return_completed" },
  { label: "Return Failed", value: "return_failed" },
  { label: "Dropoff Scheduled", value: "dropoff_scheduled" },
  { label: "Dropoff In Progress", value: "dropoff_in_progress" },
  { label: "Dropoff Completed", value: "dropoff_completed" },
  { label: "Dropoff Failed", value: "dropoff_failed" },
  { label: "Cancelled", value: "cancelled" }
];

const GeneratePoolModal = ({ visible, onCancel }) => {
  // Default to last 3 months to have a wider range
  const [invoiceDateRange, setInvoiceDateRange] = useState([
    moment().subtract(3, "months").startOf("month"),
    moment().endOf("month")
  ]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedStatuses, setSelectedStatuses] = useState(["dropoff_completed"]);
  const [emailClients, setEmailClients] = useState(true);
  const [loadingPoolPackages, setLoadingPoolPackages] = useState(false);
  const [loadingGeneration, setLoadingGeneration] = useState(false);
  const [poolData, setPoolData] = useState(null);

  useEffect(() => {
    if (visible) {
      handleLoadPoolPackages();
    }
  }, [visible]);

  // Loaded without a client filter on purpose: the table is filtered client-side and
  // the Client Filter options are built from whatever this date range returned.
  const handleLoadPoolPackages = async (
    dateRange = invoiceDateRange,
    statuses = selectedStatuses
  ) => {
    if (!dateRange || dateRange.length !== 2) {
      message.error("Please select a valid date range");
      return;
    }

    setLoadingPoolPackages(true);
    try {
      const startDate = dateRange[0].format("YYYY-MM-DD");
      const endDate = dateRange[1].format("YYYY-MM-DD");
      const response = await billingApi.getPoolPackagesForInvoice(
        startDate,
        endDate,
        statuses
      );
      setPoolData(response);
    } catch (error) {
      console.error("Failed to load pool packages:", error);
      message.error("Failed to load pool packages");
    } finally {
      setLoadingPoolPackages(false);
    }
  };

  const handleGeneratePoolInvoices = async () => {
    if (!invoiceDateRange || invoiceDateRange.length !== 2) {
      message.error("Please select a valid date range");
      return;
    }

    setLoadingGeneration(true);
    try {
      const startDate = invoiceDateRange[0].format("YYYY-MM-DD");
      const endDate = invoiceDateRange[1].format("YYYY-MM-DD");
      const response = await billingApi.generatePoolInvoices(
        startDate,
        endDate,
        selectedStatuses,
        emailClients,
        selectedClient
      );

      const { generatedCount, emailedCount } = response;
      let successMessage = `Successfully generated ${generatedCount} invoice${
        generatedCount !== 1 ? "s" : ""
      }`;
      if (emailClients && emailedCount > 0) {
        successMessage += ` and emailed ${emailedCount} client${
          emailedCount !== 1 ? "s" : ""
        }`;
      }
      message.success(successMessage);

      // Close the modal after successful generation
      onCancel();
    } catch (error) {
      console.error("Failed to generate pool invoices:", error);
      message.error("Failed to generate invoices");
    } finally {
      setLoadingGeneration(false);
    }
  };

  // Filter packages based on selected client
  const filteredPackages = poolData?.packages
    ? selectedClient
      ? poolData.packages.filter((pkg) => pkg.clientId === selectedClient)
      : poolData.packages
    : [];

  // Only the clients that actually have pool packages in this date range
  const clientsWithPackages = React.useMemo(() => {
    const byId = new Map();
    (poolData?.packages || []).forEach((pkg) => {
      if (pkg.clientId && !byId.has(pkg.clientId)) {
        byId.set(pkg.clientId, {
          id: pkg.clientId,
          clientName: pkg.clientName || "N/A"
        });
      }
    });
    return [...byId.values()].sort((a, b) => a.clientName.localeCompare(b.clientName));
  }, [poolData]);

  // Calculate filtered summary statistics
  const uniqueClients = new Set(filteredPackages.map((pkg) => pkg.clientId)).size;
  const totalPackages = filteredPackages.length;
  const totalRevenue = filteredPackages.reduce(
    (sum, pkg) => sum + (parseFloat(pkg.clientFinalPrice) || 0),
    0
  );

  return (
    <Modal
      title="Generate Pool Invoices"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width="90%"
      style={{ maxWidth: "1400px" }}
    >
      <div>
        Will generate invoices for all pool packages (PackageV2) during the date range you
        choose, filtered by selected statuses and optionally by client.
      </div>

      {/* Filters Section */}
      <Card title="Filters" style={{ marginBottom: 16, marginTop: 16 }}>
        <Row gutter={[24, 16]}>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Date Range</div>
            <MondayFirstRangePicker
              value={invoiceDateRange}
              onChange={(newRange) => {
                setInvoiceDateRange(newRange);
                // Refresh the pool packages when date range changes
                if (newRange && newRange.length === 2) {
                  handleLoadPoolPackages(newRange, selectedStatuses);
                }
              }}
              format="YYYY-MM-DD"
              style={{ width: "100%" }}
            />
          </Col>

          <Col xs={24} md={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Client Filter</div>
            <Select
              style={{ width: "100%" }}
              placeholder="All clients"
              allowClear
              showSearch
              value={selectedClient}
              onChange={setSelectedClient}
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {clientsWithPackages.map((client) => (
                <Select.Option key={client.id} value={client.id}>
                  {client.clientName}
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row gutter={[24, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Package Status</div>
            <Select
              mode="multiple"
              style={{ width: "100%" }}
              placeholder="Select statuses"
              value={selectedStatuses}
              onChange={(values) => {
                setSelectedStatuses(values);
                // Refresh the pool packages when status filter changes
                if (invoiceDateRange && invoiceDateRange.length === 2) {
                  handleLoadPoolPackages(invoiceDateRange, values);
                }
              }}
            >
              {PACKAGE_STATUS_OPTIONS.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} md={12}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Email Notifications</div>
            <div style={{ display: "flex", alignItems: "center", height: 32 }}>
              <Switch
                checked={emailClients}
                onChange={setEmailClients}
                style={{ marginRight: 8 }}
              />
              <span>{emailClients ? "Email clients" : "No emails"}</span>
            </div>
          </Col>
        </Row>

        <Row gutter={[24, 16]} style={{ marginTop: 16 }}>
          <Col xs={24}>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={handleGeneratePoolInvoices}
              loading={loadingGeneration}
              style={{ width: "100%", height: 40 }}
              size="large"
            >
              Generate Invoices
            </Button>
          </Col>
        </Row>
      </Card>

      {poolData && (
        <>
          {/* Summary Section */}
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={6}>
                <Statistic
                  title="Date Range"
                  value={poolData.summary.dateRange}
                  valueStyle={{ fontSize: 14, color: "#666" }}
                />
              </Col>
              <Col xs={24} sm={6}>
                <Statistic
                  title="Total Clients"
                  value={uniqueClients}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Col>
              <Col xs={24} sm={6}>
                <Statistic
                  title="Total Packages"
                  value={totalPackages}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Col>
              <Col xs={24} sm={6}>
                <Statistic
                  title="Total Revenue (est.)"
                  value={totalRevenue}
                  precision={2}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: "#722ed1" }}
                />
              </Col>
            </Row>
          </Card>

          {/* Packages Table */}
          <Table
            dataSource={filteredPackages}
            rowKey="id"
            pagination={{ pageSize: 20 }}
            scroll={{ x: 800 }}
            loading={loadingPoolPackages}
            onRow={(record) => ({
              onClick: () => {
                window.open(`/admin/packages/${record.id}`, "_blank");
              }
            })}
            style={{ cursor: "pointer" }}
            columns={[
              {
                title: "ID",
                dataIndex: "id",
                width: 120,
                render: (value) => (
                  <span style={{ color: "#1f3998", fontWeight: 500 }}>
                    {value.substr(0, 8).toUpperCase()}
                  </span>
                )
              },
              {
                title: "Client",
                dataIndex: "clientName",
                width: 200,
                sorter: (a, b) => a.clientName.localeCompare(b.clientName)
              },
              {
                title: "Pickup Address",
                dataIndex: "pickupAddress",
                width: 200,
                ellipsis: true
              },
              {
                title: "Dropoff Address",
                dataIndex: "dropoffAddress",
                width: 200,
                ellipsis: true
              },
              {
                title: "Status",
                dataIndex: "status",
                width: 150,
                render: (value) => (
                  <span
                    style={{
                      textTransform: "capitalize",
                      color:
                        value === "Dropoff Completed"
                          ? "#52c41a"
                          : value === "Cancelled"
                          ? "#ff4d4f"
                          : "#1890ff"
                    }}
                  >
                    {value}
                  </span>
                )
              },
              {
                title: "Client Final Price",
                dataIndex: "clientFinalPrice",
                width: 150,
                align: "right",
                render: (value) => (
                  <span style={{ fontWeight: 500 }}>
                    ${parseFloat(value || 0).toFixed(2)}
                  </span>
                )
              }
            ]}
          />
        </>
      )}
    </Modal>
  );
};

export default GeneratePoolModal;
